import { type AxiosError } from 'axios'
import { makeAutoObservable } from 'mobx'

import { ServicesController } from '@App/api'
import { type IApiErrorHandler, type ISwapSequencs } from '@App/types/common'
import {
    type ITransferServiceDto,
    type ICategoryDto,
    type IChildServiceDec,
    type IServiceDec,
    type IServicesFilters,
    type IServiceRulesWrapper,
    type ISectionRulesWrapper
} from '@App/types/servicesTypes'
import { onHandleServicesListAdditionsProps, onHandleResponseNotification, onUseQueries } from '@App/utils'
import { defaultServicesFilters } from '@Config/config'

class Services {
    search: string = ''
    servicesFilters: IServicesFilters = defaultServicesFilters
    categoriesList: IServiceDec[] | null = null
    tranferSevice: IChildServiceDec | null = null

    serviceRules: IServiceRulesWrapper | null = null
    sectionRules: ISectionRulesWrapper | null = null

    isFetching: boolean = false

    constructor() {
        makeAutoObservable(this)
    }

    // actions
    onSetServiceRules(serviceRules: IServiceRulesWrapper | null) {
        this.serviceRules = serviceRules
    }

    onSearch(search: string) {
        this.search = search
    }

    onSetSectionRules = (sectionRules: ISectionRulesWrapper | null) => {
        this.sectionRules = sectionRules
    }

    onSetTranferSevice = (tranferSevice: IChildServiceDec | null) => {
        this.tranferSevice = tranferSevice
    }

    onSetServicesFilters(filters: IServicesFilters) {
        this.servicesFilters = filters
    }

    onSetCategoriesList(data: IServiceDec[] | null) {
        this.categoriesList = data
    }

    onSetIsFetching(isFetching: boolean) {
        this.isFetching = isFetching
    }

    // requests
    async onGetAllCategories() {
        const client = new ServicesController()
        try {
            const { data } = await client.onGetAllCategories()
            this.onSetCategoriesList(
                onHandleServicesListAdditionsProps(
                    data.categories.sort((a: IServiceDec, b: IServiceDec) => a.sequence - b.sequence)
                )
            )
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    async onAddCategory(params: IOnAddCategoryParams) {
        const { dto, onSuccess } = params
        const client = new ServicesController()
        try {
            const { status } = await client.onAddCategory(dto)
            if (status < 300 && status >= 200) {
                onSuccess()
                this.onSetCategoriesList(null)
                this.onGetAllCategories()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    async onEditCategory(params: IOnEditCategoryParams) {
        const { dto, onSuccess } = params
        const client = new ServicesController()
        try {
            const { status } = await client.onEditCategory(dto)
            if (status < 300 && status >= 200) {
                onSuccess()
                this.onSetCategoriesList(null)
                this.onGetAllCategories()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    async onRemoveCategory(params: IOnRemoveCategoryParams) {
        const { id, onSuccess } = params
        const client = new ServicesController()
        const queries = onUseQueries({ categoryId: id })
        try {
            const { status } = await client.onRemoveCategory(queries)
            if (status === 204) {
                onSuccess()
                this.onSetCategoriesList(null)
                this.onGetAllCategories()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    async onTransferService(params: IOnTransferServiceParams) {
        const { dto, onSuccess } = params
        const client = new ServicesController()
        try {
            const { status } = await client.onTransferService(dto)
            if (status === 204) {
                onSuccess()
                this.onSetCategoriesList(null)
                this.onGetAllCategories()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    async onGetServiceRules(code: string) {
        this.onSetIsFetching(true)
        const client = new ServicesController()
        try {
            const { data } = await client.onGetServiceRules(code)
            this.onSetServiceRules({ ...data, code })
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    async onChangePreparingServiceRulesData(params: IOnChangePreparingServiceRulesParams) {
        this.onSetIsFetching(true)
        const { dto, onSuccess, withFetching = true } = params
        const client = new ServicesController()
        try {
            const { status } = await client.onChangePreparingServiceRulesData(dto)
            if (status === 204) {
                onSuccess?.()
                this.onGetServiceRules(dto.code)
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            withFetching && this.onSetIsFetching(false)
        }
    }

    async onGetSectionsRules(id: string) {
        this.onSetIsFetching(true)
        const client = new ServicesController()
        try {
            const { data } = await client.onGetSectionsRules(id)
            this.onSetSectionRules({ ...data, id })
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    async onChangePreparingRulesData(params: IOnChangePreparingRulesParams) {
        this.onSetIsFetching(true)
        const { dto, onSuccess, withFetching = true } = params
        const client = new ServicesController()
        try {
            const { status } = await client.onChangePreparingRulesData(dto)
            if (status === 204) {
                onSuccess?.()
                this.onGetSectionsRules(dto.id)
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            withFetching && this.onSetIsFetching(false)
        }
    }

    async onSwapCategories(params: IOnSwapCategories) {
        const { dto } = params
        const client = new ServicesController()
        try {
            const { status } = await client.onSwapCategories(dto)
            if (status === 204) {
                this.onSetCategoriesList(null)
                this.onGetAllCategories()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    async onSwapServices(params: IOnSwapServices) {
        const { dto } = params
        const client = new ServicesController()
        try {
            const { status } = await client.onSwapServices(dto)
            if (status === 204) {
                this.onSetCategoriesList(null)
                this.onGetAllCategories()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }
}

export default new Services()

// types
interface IOnAddCategoryParams {
    dto: ICategoryDto
    onSuccess: () => void
}

interface IOnEditCategoryParams {
    dto: ICategoryDto
    onSuccess: () => void
}

interface IOnRemoveCategoryParams {
    id: string
    onSuccess: () => void
}

interface IOnTransferServiceParams {
    dto: ITransferServiceDto
    onSuccess: () => void
}

interface IOnChangePreparingRulesParams {
    dto: ISectionRulesWrapper
    onSuccess?: () => void
    withFetching?: boolean
}

interface IOnChangePreparingServiceRulesParams {
    dto: IServiceRulesWrapper
    onSuccess?: () => void
    withFetching?: boolean
}

interface IOnSwapCategories {
    dto: ISwapSequencs
}

interface IOnSwapServices {
    dto: ISwapSequencs
}
